import { useQuery } from '@tanstack/react-query'
import { Link } from 'react-router-dom'
import { StatFilters, StatGroupKey, StatTimeseriesResponse } from '@/models/stats.ts'
import { useClient } from '@/stores/useClient.ts'
import { STAT_GROUP_COLORS } from './statColors.ts'

interface StatSparklineProps {
  group: StatGroupKey
  label: string
  days?: number
  filters?: StatFilters
  /** Where the tile links to. Omit for a non-interactive tile. */
  to?: string
}

/** 'YYYY-MM-DD' in local time. */
function isoDaysAgo(days: number): string {
  const date = new Date()
  date.setDate(date.getDate() - (days - 1))
  const month = String(date.getMonth() + 1).padStart(2, '0')
  const day = String(date.getDate()).padStart(2, '0')
  return `${date.getFullYear()}-${month}-${day}`
}

/**
 * One number plus its trend. A single series, so it needs no legend — the label
 * below the number names it.
 */
export default function StatSparkline({ group, label, days = 14, filters, to }: StatSparklineProps) {
  const { getStatsTimeseries } = useClient()
  const filterKey = JSON.stringify(filters ?? {})
  const color = STAT_GROUP_COLORS[group]

  const { data, isLoading } = useQuery({
    queryKey: ['stats', 'sparkline', group, days, filterKey],
    queryFn: async () => (await getStatsTimeseries({
      ...filters,
      from: isoDaysAgo(days),
      interval: 'day',
      group: [group],
    })).data as StatTimeseriesResponse,
  })

  const values = data?.series[0]?.values ?? []
  const total = data?.series[0]?.total ?? 0
  const max = Math.max(1, ...values)
  const points = values.length > 1
    ? values.map((value, index) => `${(index / (values.length - 1)) * 100},${30 - (value / max) * 28}`).join(' ')
    : null

  const content = (
    <>
      <div className='text-3xl font-serif tabular-nums'>{isLoading ? '–' : total}</div>
      <div className='text-xs opacity-60 mt-0.5 mb-3'>{label} · {days} Tage</div>
      <svg
        viewBox='0 0 100 30'
        preserveAspectRatio='none'
        className='w-full h-9 block overflow-visible'
        aria-hidden='true'
      >
        {points ? (
          <polyline
            points={points}
            fill='none'
            stroke={color}
            strokeWidth={2}
            vectorEffect='non-scaling-stroke'
            strokeLinejoin='round'
            strokeLinecap='round'
          />
        ) : (
          <line x1='0' y1='29' x2='100' y2='29' stroke='var(--portal-border)' strokeWidth={2} vectorEffect='non-scaling-stroke' />
        )}
      </svg>
    </>
  )

  const className = 'bg-neutral border border-[var(--portal-border)] p-4 block'

  if (!to) {
    return <div className={className}>{content}</div>
  }

  return (
    <Link to={to} className={`${className} hover:shadow-md transition-shadow`}>
      {content}
    </Link>
  )
}
